import { useCallback, useEffect, useRef, useState } from 'react';

export type AutosaveStatus = 'idle' | 'saving' | 'saved' | 'error' | 'stale';

interface SaveOk {
  ok: true;
  doc_version: number;
  updated_at?: number;
}

interface SaveConflict {
  ok: false;
  reason: string;
  status: number;
  error: string;
}

type SaveResult = SaveOk | SaveConflict;
type SaveFn<T> = (value: T) => Promise<SaveResult>;

interface Options<T> {
  /** Current snapshot to autosave. */
  value: T;
  /** Save function, typically a thin wrapper around an `adminApi` call. */
  save: SaveFn<T>;
  /** Pause autosaving entirely (locked/read-only forms, an active conflict). */
  enabled?: boolean;
  /** Skip saving on initial mount (the load call). */
  skipFirst?: boolean;
  /** Debounce delay in ms. */
  delayMs?: number;
  /** Fired after every successful save (debounced autosave or `flush()`). */
  onSaved?: (result: SaveOk) => void;
  /** Fired on a 409 conflict, carrying the server's `reason`. */
  onConflict?: (result: SaveConflict) => void;
}

/**
 * Debounced autosave for the proposal editor. Skips the request entirely
 * when the snapshot hasn't changed since the last successful save, and
 * distinguishes a save conflict (409) from a network/validation error so the
 * caller can react to `reason` ('stale' vs 'signed' vs 'not-signed').
 *
 * Saves are serialized: at most one request is ever in flight. Edits that
 * arrive while a save is in flight are collapsed (only the latest snapshot
 * survives) and picked up as a single follow-up save once the current one
 * settles, so a single tab can never fire two overlapping saves and can
 * never self-inflict a stale conflict by racing itself.
 */
export function useAutosave<T>({
  value,
  save,
  enabled = true,
  skipFirst = true,
  delayMs = 1500,
  onSaved,
  onConflict,
}: Options<T>) {
  const [status, setStatus] = useState<AutosaveStatus>('idle');
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedJson = useRef<string>(JSON.stringify(value));
  const isFirst = useRef(true);

  // Serialization. At most one save request is ever in flight (inFlightRef
  // holds its promise). A snapshot that arrives while one is running just
  // overwrites queuedRef (only the latest survives) and is picked up by the
  // same in-flight loop once the current request settles, rather than
  // firing a second overlapping request.
  const queuedRef = useRef<{ value: T } | null>(null);
  const inFlightRef = useRef<Promise<SaveResult | null> | null>(null);

  const runSave = useCallback(
    async (snapshot: T): Promise<SaveResult> => {
      setStatus('saving');
      setErrorMessage(null);
      try {
        const result = await save(snapshot);
        if (!result.ok) {
          setStatus('stale');
          setErrorMessage(result.error);
          onConflict?.(result);
          return result;
        }
        lastSavedJson.current = JSON.stringify(snapshot);
        setStatus('saved');
        setSavedAt(result.updated_at ?? Date.now());
        onSaved?.(result);
        return result;
      } catch (caught) {
        const message = caught instanceof Error ? caught.message : 'Save failed.';
        setStatus('error');
        setErrorMessage(message);
        // Give thrown errors the same { ok: false } shape as a conflict so
        // every caller (drain, flush, its consumers) can just check `.ok`.
        return { ok: false, reason: 'error', status: 0, error: message };
      }
    },
    [save, onSaved, onConflict],
  );

  /** Drains queuedRef until it's empty, running at most one save at a time.
   *  Stops (without clearing anything further that arrives) as soon as a
   *  save comes back not-ok, so a conflict or error isn't immediately
   *  hammered again with the same rejected content. */
  const drain = useCallback(async (): Promise<SaveResult | null> => {
    let lastResult: SaveResult | null = null;
    for (;;) {
      const queued = queuedRef.current;
      queuedRef.current = null;
      if (!queued) break;
      if (JSON.stringify(queued.value) === lastSavedJson.current) continue;
      lastResult = await runSave(queued.value);
      if (!lastResult.ok) break;
    }
    return lastResult;
  }, [runSave]);

  /** Queue a snapshot to be saved and return the promise for the drain loop
   *  that will (eventually) save it. If a loop is already running, this
   *  just replaces what's queued and rides the existing loop's promise. */
  const enqueue = useCallback(
    (snapshot: T): Promise<SaveResult | null> => {
      queuedRef.current = { value: snapshot };
      if (!inFlightRef.current) {
        inFlightRef.current = drain().finally(() => {
          inFlightRef.current = null;
        });
      }
      return inFlightRef.current;
    },
    [drain],
  );

  useEffect(() => {
    if (!enabled) return;
    if (skipFirst && isFirst.current) {
      isFirst.current = false;
      lastSavedJson.current = JSON.stringify(value);
      return;
    }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      void enqueue(value);
    }, delayMs);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, delayMs, enqueue, skipFirst, enabled]);

  /** Save the current value now, waiting for any in-flight save (and any
   *  saves that get queued behind it) to fully settle first. Returns the
   *  final outcome: `null` if there was nothing to save, otherwise the last
   *  save's result, so callers can abort an action when it comes back
   *  not-ok. */
  const flush = useCallback(async () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    return enqueue(value);
  }, [enqueue, value]);

  /** Reset the "last saved" baseline without a network call, used after a
   *  restore or an external doc replacement so autosave doesn't immediately
   *  try to re-save the freshly loaded state. Also drops anything queued for
   *  the doc being replaced; an already in-flight request still completes,
   *  but nothing further is saved on top of the state the caller just
   *  adopted. */
  const adopt = useCallback((snapshot: T, at?: number) => {
    queuedRef.current = null;
    lastSavedJson.current = JSON.stringify(snapshot);
    setStatus('idle');
    setErrorMessage(null);
    if (at !== undefined) setSavedAt(at);
  }, []);

  const isDirty = JSON.stringify(value) !== lastSavedJson.current;

  return { status, savedAt, errorMessage, flush, adopt, isDirty };
}
